import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/header/Header"; // ← Your real header
import TestimonialsSection from "../../components/testimonialsSection/TestimonialsSection";
import FAQSection from "../../components/FAQSection/FAQSection";
import Footer from "../../components/footer/Footer";
import BackToTop from "../../components/backToTop/BackToTop";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./AboutPage.css";

const AboutPage = () => {
  const [activeTab, setActiveTab] = useState("mission"); // mission | vision
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { user } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top smoothly when the component mounts / route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth" if you prefer a gentle scroll
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>About Us | TutorsLinks</title>
        <meta
          name="description"
          content="TutorsLinks helps learners across Nigeria find trusted tutors and training centres for personalised learning, exam preparation and adult education."
        />
      </Helmet>

      <Header
        isMenuOpen={isMenuOpen}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
      />

      {/* ===== HERO ABOUT SECTION ===== */}
      <section className="about-hero">
        <div className="about-container about-hero-grid">
          <div className="about-hero-left">
            <h1 className="about-heading">About Us</h1>
            <p className="about-subtitle">
              TutorsLinks helps learners across Nigeria find trusted tutors
              and training centres for personalised learning, exam
              preparation and adult education. We make it easier to discover
              learning support based on subject, location, delivery mode,
              budget and verified credentials.
            </p>
            {!user && (
              <button
                className="btn-primary"
                onClick={() => navigate("/decision-screen")}
              >
                Get Started <span className="arrow">→</span>
              </button>
            )}
          </div>

          {/* ── RIGHT: Video ── */}
          <div className="about-hero-right">
            <div className="about-video-wrapper">
              <video
                src="/videos/about-tutorslinks.mp4"
                controls
                playsInline
                className="about-video"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* ===== WHO WE SERVE SECTION ===== */}
      <section className="who-we-serve-section">
        <div className="about-container">
          <div className="who-we-serve-grid">
            <div className="who-we-serve-card">
              <h3>For Learners</h3>
              <p>Find the right learning support.</p>
            </div>
            <div className="who-we-serve-card">
              <h3>For Tutors</h3>
              <p>Build visibility and earn from your expertise.</p>
            </div>
            <div className="who-we-serve-card">
              <h3>For Centres</h3>
              <p>Reach learners looking for your programmes.</p>
            </div>
            <div className="who-we-serve-card who-we-serve-focus">
              <h3>Our Focus</h3>
              <p>JAMB, WAEC/NECO, Adult Education and broader learning needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MISSION & VISION SECTION - FINAL VERSION ===== */}
      <section className="mission-vision-section">
        <div className="mission-container mv-main-grid">
          {/* LEFT: Vertical Toggle Buttons */}
          <div className="mv-vertical-tabs">
            <button
              className={`mv-tab-btn ${
                activeTab === "mission" ? "active" : ""
              }`}
              onClick={() => setActiveTab("mission")}
            >
              Our Mission
            </button>
            <button
              className={`mv-tab-btn ${activeTab === "vision" ? "active" : ""}`}
              onClick={() => setActiveTab("vision")}
            >
              Our Vision
            </button>
          </div>

          {/* CENTER + RIGHT: Dynamic Content (Image + Text) */}
          <div
            className={`mv-content-grid ${
              activeTab === "vision" ? "vision-active" : ""
            }`}
          >
            {/* Image with Overlay */}
            <div className="mv-image-wrapper">
              <img
                src={
                  activeTab === "mission"
                    ? "/images/mission-image.jpg"
                    : "/images/study-group3.jpg"
                }
                alt={activeTab === "mission" ? "Our Mission" : "Our Vision"}
              />
              {/* <div className="mv-image-overlay">
                <h3>
                  {activeTab === "mission"
                    ? "In a world where education is key, the sun rises over a landscape filled with eager learners. The gentle breeze carries the hopes of students seeking guidance, while vibrant classrooms buzz with activity, creating a tapestry of knowledge. As the day progresses, the laughter of students resonates, echoing through halls of learning. Each lesson is a step on the path to success, encouraging us to embrace the resources available. From bustling online platforms to welcoming learning centers, every option offers a chance to connect with professional tutors, ensuring that every student can thrive."
                    : "We do what’s right, even when no one is watching. Our commitment to honesty, transparency, and strong ethics builds lasting trust with our team, users, and partners."}
                </h3>
              </div> */}
            </div>

            {/* Text Content */}
            <div className="mv-text-wrapper">
              <div className="mv-statement">
                {activeTab === "mission" ? (
                  <>
                    <h3>Our Mission</h3>
                    <p>
                      We don’t wait for change, we drive it. At TutorsLinks,
                      leadership means stepping up, taking responsibility, and
                      creating opportunities for others to rise alongside us.
                    </p>
                  </>
                ) : (
                  <>
                    <h3>Our Vision</h3>
                    <p>
                      We do what’s right, even when no one is watching. Our
                      commitment to honesty, transparency, and strong ethics
                      builds lasting trust with our team, users, and partners
                      throughout the platform.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== OUR VALUES SECTION ===== */}
      <section className="values-section">
        <div className="values-grid-container">
          <div className="values-left">
            <h2>Our Values</h2>
            <p>
              At Tutorslinks, we value connecting students with exceptional
              tutors, creating a platform where knowledge is shared, and
              academic success is achieved. We also empower tutors to showcase
              their expertise and be rewarded for their invaluable
              contributions.
            </p>
          </div>

          <div className="values-right">
            <div className="values-cards">
              {[
                { label: "Academic Quality" },
                { label: "Empowerment & Reach" },
                { label: "Innovation & Flexibility" },
                { label: "Personalization & Flexibility" },
              ].map(({ label }) => (
                <div className="value-card" key={label}>
                  <div className="values-badge">
                    <img src="/images/values-image.png" alt={label} />
                  </div>
                  <div className="value-title-container">
                    <hr className="value-divider" />
                    <h4>{label}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <FAQSection />
      <Footer />
      <BackToTop />
    </>
  );
};

export default AboutPage;
